// v237 admin API client for the demo-data seeder (item 58 of
// PRODUCT-ROADMAP.md, item 67 in the recommended delivery order).
// Backs the AdminSeed page and the SeedBanner strip.

import useSWR from 'swr';

export interface SeedState {
  enabled: boolean;
  set_name?: string;
  applied_at?: string;     // RFC3339
  applied_by?: string;
  item_count: number;
}

export interface SeedPlan {
  set: string;
  projects: string[];
  repositories: string[];
  service_accounts: string[];
  security_blocks: string[];
  audit_events: number;
  // Phase A v2 expansion (item 66 / delivery #75) — counts of the
  // additional fourteen entity classes the v2 bundle writes.
  banners: number;
  notification_channels: number;
  webhook_subscriptions: number;
  retention_policies: number;
  scan_rules: number;
  oidc_trust_policies: number;
  sigstore_trust_policies: number;
  signing_keys: number;
  pull_gate_policies: number;
  quarantine_settings: number;
  geosync_peers: number;
  backup_verification_runs: number;
  cnpg_catalog_entries: number;
  license_policies: number;
}

export interface SeedApplyResult {
  set: string;
  item_count: number;
  skipped?: number;
}

export interface SeedResetResult {
  deleted: number;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useSeedStatus() {
  return useSWR<SeedState>('/api/admin/seed/status', json, {
    refreshInterval: 30_000,
  });
}

export function useSeedPreview(set: string | null) {
  return useSWR<SeedPlan>(
    set ? `/api/admin/seed/preview?set=${encodeURIComponent(set)}` : null,
    json,
    { revalidateOnFocus: false },
  );
}

export async function applySeed(input: { set: string; force?: boolean }) {
  const res = await fetch('/api/admin/seed/apply', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return (await res.json()) as SeedApplyResult;
}

export async function resetSeed() {
  const res = await fetch('/api/admin/seed/reset', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return (await res.json()) as SeedResetResult;
}
